import * as React from "react";
import { CheckCircle2, AlertTriangle, XCircle, Info, Clock } from "lucide-react";
import { Pill } from "./Pill";
import { healthFromRag, ASK_STATE, ENTITY_STATE, type StatusTone } from "@/domain/status";
import type { SpendState } from "@/domain/types";

// The only way status renders (design brief §5.2): colour + dot/icon + words, from the
// canonical vocabulary in domain/status.ts. Tone maps to the hardened Pill variant; the
// icon set is fixed so a tone always reads the same.
const TONE_TO_VARIANT: Record<StatusTone, React.ComponentProps<typeof Pill>["variant"]> = {
  success: "success",
  warning: "warn",
  danger: "danger",
  info: "info",
  pending: "neutral",
};

const TONE_ICON: Record<StatusTone, React.ElementType> = {
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: XCircle,
  info: Info,
  pending: Clock,
};

export function StatusPill({
  tone,
  label,
  withIcon = false,
  size,
  className,
}: {
  tone: StatusTone;
  label: string;
  /** Use the semantic glyph instead of the leading dot. */
  withIcon?: boolean;
  size?: "sm" | "md";
  className?: string;
}) {
  const Icon = TONE_ICON[tone];
  return (
    <Pill
      variant={TONE_TO_VARIANT[tone]}
      size={size}
      className={className}
      icon={withIcon ? <Icon className="h-3 w-3" aria-hidden /> : undefined}
    >
      {label}
    </Pill>
  );
}

/** Health from a RAG value, rendered as On track / Watch / At risk. */
export function HealthPill({ rag, withIcon }: { rag: "GREEN" | "AMBER" | "RED"; withIcon?: boolean }) {
  const d = healthFromRag(rag);
  return <StatusPill tone={d.tone} label={d.label} withIcon={withIcon} />;
}

/** Spend ask lifecycle pill from the canonical vocabulary. */
export function AskStatePill({ state }: { state: SpendState }) {
  const d = ASK_STATE[state];
  return <StatusPill tone={d.tone} label={d.label} />;
}

/**
 * Project / sub-project status pill, covering both the legacy vocabulary
 * (active/planned/closed) and the ZEUS governance vocabulary
 * (draft/review/published/archived) via the shared ENTITY_STATE map.
 */
export function EntityStatusPill({ status }: { status: string }) {
  const d = ENTITY_STATE[status] ?? { label: status, tone: "pending" as const };
  return <StatusPill tone={d.tone} label={d.label} />;
}
